import React, { FC, ReactNode, ComponentType } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import clsx from "clsx";
import { IconButton } from "components/ui";
import { useHorizontalScroll } from "hooks/use-horizontal-scroll";
import { ReactComponent as ChevronIcon } from "Assets/icon/icon-chevron.svg";
import { UrlSearchParams } from "types/enums";
import { trackEvent } from "utility/mixpanel";
import { useTranslation } from "lib/i18n/use-translation";

import styles from "./tabs.module.scss";

export interface TabType {
  key: string;
  label: string;
  content: ReactNode;
  icon?: ComponentType<any>;
}

enum TabEventName {
  TabClick = "Tab Clicked",
}

enum TabEventPropertyKeys {
  TabName = "Tab Name",
  Level = "Level",
  PreviousTab = "Previous Tab",
}

enum TabEventPropertyValues {
  LevelPrimary = "primary",
}

export interface TabsPropsType {
  tabs: TabType[];
  activeTab: string;
  onTabChange: (tabKey: string) => void;
  className?: string;
  tabsClassName?: string;
  contentClassName?: string;
  rightElement?: React.ReactNode;
  showScrollButtons?: boolean;
}
// Removed duplicate TabsPropsType interface declaration

const Tabs: FC<TabsPropsType> = ({
  tabs,
  activeTab,
  onTabChange,
  className,
  tabsClassName,
  contentClassName,
  rightElement,
  showScrollButtons = false,
}) => {
  const { t } = useTranslation("components");
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Use horizontal scroll hook for tab headers
  const {
    containerRef: tabHeadersRef,
    canScrollLeft,
    canScrollRight,
    scrollToEdge,
  } = useHorizontalScroll({ deps: [tabs, showScrollButtons] });

  const handleScrollLeft = () => {
    scrollToEdge("left");
  };
  const handleScrollRight = () => {
    scrollToEdge("right");
  };

  const createTabUrl = (tabKey: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set(UrlSearchParams.Tab, tabKey);
    return `${location.pathname}?${newSearchParams.toString()}`;
  };

  const handleTabClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    tabKey: string
  ) => {
    event.preventDefault();
    trackEvent(TabEventName.TabClick, {
      [TabEventPropertyKeys.TabName]: tabKey,
      [TabEventPropertyKeys.Level]: TabEventPropertyValues.LevelPrimary,
      [TabEventPropertyKeys.PreviousTab]: activeTab,
    });
    onTabChange(tabKey);
  };

  const activeTabContent = tabs.find((tab) => tab.key === activeTab)?.content;

  return (
    <div className={clsx(styles.tabsContainer, className)}>
      <div className={clsx(styles.tabsList, tabsClassName)}>
        {showScrollButtons && (
          <div className={styles.tabsScrollButtons}>
            <IconButton
              className={styles.tabsScrollButton}
              aria-label={t("components:tabs.scrollLeftA11y")}
              onClick={handleScrollLeft}
              disabled={!canScrollLeft}
              icon={<ChevronIcon className={styles.tabsChevronLeft} />}
            />
          </div>
        )}
        <div
          className={clsx(
            styles.tabHeaders,
            showScrollButtons && styles.tabHeadersScrollable
          )}
          ref={tabHeadersRef}
        >
          {tabs.map((tab) => (
            <a
              key={tab.key}
              href={createTabUrl(tab.key)}
              className={clsx(
                styles.tab,
                activeTab === tab.key && styles.tabActive
              )}
              onClick={(event) => handleTabClick(event, tab.key)}
            >
              {tab.icon && (
                <tab.icon className={styles.tabIcon} width={16} height={16} />
              )}
              <span className={styles.tabLabel}>{tab.label}</span>
            </a>
          ))}
        </div>
        {showScrollButtons && (
          <div className={styles.tabsScrollButtons}>
            <IconButton
              className={styles.tabsScrollButton}
              aria-label={t("components:tabs.scrollRightA11y")}
              onClick={handleScrollRight}
              disabled={!canScrollRight}
              icon={<ChevronIcon />}
              variant="ghost"
            />
          </div>
        )}
        {rightElement && (
          <div className={styles.rightElement}>{rightElement}</div>
        )}
      </div>
      <div className={clsx(styles.tabContent, contentClassName)}>
        {activeTabContent}
      </div>
    </div>
  );
};

export default Tabs;
